import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useUserProfile } from '../../../contexts/UserProfileContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000'; // Still needed for image src
const API_BASE = API_URL === '/api' ? '' : API_URL;

const ProfileSettingsContainer = styled(motion.div)`
  padding: 2.5rem;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(76, 110, 245, 0.15);
  max-width: 700px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 2rem; /* Slightly larger */
  color: #2c3e50; /* Dark blue-gray */
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
`;

const ProfileContainer = styled.div`
  display: flex;
  gap: 2.5rem; // Increased gap for better separation
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
`;

const FormFieldsSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem; // Space between form groups
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem; /* Increased gap */
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568; /* Medium gray */
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #cbd5e0; /* Lighter border */
  border-radius: 8px;
  font-size: 1rem;
  color: #2d3748; /* Darker text */
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4c6ef5; /* Primary blue */
    box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.2);
  }

  &:read-only {
    background-color: #f7fafc; /* Light gray for read-only */
    color: #718096; /* Muted text for read-only */
    cursor: not-allowed;
  }
`;

const ProfileImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem; /* Space between image and button */
  min-width: 150px; /* Give it some base width */
  max-width: 200px; /* And a max width */
`;

const ProfileImagePreview = styled.div`
  width: 150px; /* Fixed size for the circle */
  height: 150px;
  border-radius: 50%;
  background-color: #e2e8f0; /* Light gray placeholder */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  font-size: 0.9rem;
  text-align: center;
  border: 4px solid #ffffff; /* White border for 'inset' look */
  box-shadow: 0 4px 12px rgba(0,0,0,0.15); /* Softer shadow */
  overflow: hidden; /* Ensures image stays circular */

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadButton = styled.button`
  padding: 0.7rem 1.2rem;
  background-color: #4A5568; /* Darker Gray */
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  width: 100%; /* Make button take full width of its container */

  &:hover {
    background-color: #2D3748; /* Even Darker Gray */
  }

  &:disabled {
    background-color: #A0AEC0; /* Lighter gray when disabled */
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const SubmitButton = styled(motion.button)`
  padding: 0.9rem 1.5rem;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%); /* Gradient */
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem; /* Spacing above save button */
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(76, 110, 245, 0.3);
  }
`;

const Message = styled(motion.div)<{ type: 'error' | 'success' | 'info' }>`
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  background-color: ${props => 
    props.type === 'error' ? '#ffebee' : 
    props.type === 'success' ? '#e8f5e9' : 
    '#e3f2fd'};
  color: ${props => 
    props.type === 'error' ? '#c62828' : 
    props.type === 'success' ? '#2e7d32' : 
    '#01579b'}; 
  border: 1px solid transparent;
  border-color: ${props => 
    props.type === 'error' ? '#ffcdd2' : 
    props.type === 'success' ? '#c8e6c9' : 
    '#b3e5fc'};
`;

const UserProfileSettings: React.FC = () => {
  const {
    fullName: contextFullName,
    email: contextEmail,
    phoneNumber: contextPhoneNumber,
    phoneNumberHistory: contextPhoneNumberHistory,
    profileImageUrl: contextProfileImageUrl,
    animatingImageUrl: contextAnimatingImageUrl,
    isLoadingProfile,
    isSavingProfile,
    isUploadingImage,
    uploadError,
    profileUpdateError,
    successMessage,
    updateProfileDetails,
    startImageUploadAnimation,
    processImageUpload,
    completeImageAnimation,
    clearMessages,
  } = useUserProfile();

  const [currentFullName, setCurrentFullName] = useState('');
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentFullName(contextFullName || '');
  }, [contextFullName]);

  useEffect(() => {
    setCurrentPhoneNumber(contextPhoneNumber || '');
  }, [contextPhoneNumber]);

  useEffect(() => {
    // Clear messages when component unmounts or before a new action
    return () => {
      clearMessages(); 
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await updateProfileDetails({ fullName: currentFullName, phoneNumber: currentPhoneNumber });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    clearMessages();
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        // Context will handle setting this error if processImageUpload is designed for it,
        // or we set a local error if context doesn't cover pre-upload validation messages.
        // For now, assuming context's uploadError can be used or a local one if needed.
        // For simplicity, this example won't set a specific local error for this, relying on context or future enhancement.
        alert('Invalid file type. Please select an image (jpeg, png, gif).'); // Simple alert for now
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
        alert('File is too large. Maximum size is 5MB.'); // Simple alert for now
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      
      const objectUrl = URL.createObjectURL(file);
      startImageUploadAnimation(objectUrl);
      const uploadSuccess = await processImageUpload(file, objectUrl);
      if (!uploadSuccess) {
        // If processImageUpload itself failed (e.g., network error before backend could respond with specific message)
        // and set an error in context, that will be displayed.
        // If it failed and didn't set an error (e.g. returned false due to pre-check), we might need local error handling.
        // For now, context's uploadError should cover most backend/network issues.
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      // No need to clear fileInputRef on success here, as context handles image update and message.
    } 
  };

  const combinedError = profileUpdateError || uploadError;

  return (
    <ProfileSettingsContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Title>Profile Settings</Title>
      {combinedError && <Message type="error">{combinedError}</Message>}
      {successMessage && <Message type="success">{successMessage}</Message>}
      
      {isLoadingProfile ? (
        <Message type="info">Loading profile...</Message>
      ) : (
      <Form onSubmit={handleSubmit}>
        <ProfileContainer>
          <ProfileImageSection>
            <ProfileImagePreview>
              {contextAnimatingImageUrl ? (
                <motion.div
                  key={contextAnimatingImageUrl} // Animate on new preview
                  style={{ width: '100%', height: '100%' }}
                  initial={{ clipPath: 'inset(100% 0px 0px 0px)' }}
                  animate={{ clipPath: 'inset(0% 0px 0px 0px)' }}
                  transition={{ duration: 3, ease: [0.25, 0.85, 0.45, 0.95] }}
                  onAnimationComplete={() => {
                    if (contextAnimatingImageUrl) { // Ensure it's still the one animating
                      completeImageAnimation(contextAnimatingImageUrl);
                    }
                  }}
                >
                  <img src={contextAnimatingImageUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </motion.div>
              ) : contextProfileImageUrl ? (
                <img 
                  src={`${API_BASE}${contextProfileImageUrl}`} 
                  alt="Profile" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              ) : (
                'No Image'
              )}
            </ProfileImagePreview>
            <input 
              type="file" 
              accept="image/jpeg,image/png,image/gif,image/jpg" 
              style={{ display: 'none' }} 
              ref={fileInputRef} 
              onChange={handleFileChange} 
            />
            <UploadButton 
              type="button" 
              onClick={() => fileInputRef.current?.click()} 
              disabled={isUploadingImage || isSavingProfile || isLoadingProfile}
            >
              {isUploadingImage ? 'Uploading...' : 'Change Picture'}
            </UploadButton>
          </ProfileImageSection>

          <FormFieldsSection>
            <FormGroup>
              <Label htmlFor="fullName">Full Name</Label>
              <Input 
                type="text" 
                id="fullName" 
                value={currentFullName} 
                onChange={(e) => setCurrentFullName(e.target.value)} 
                disabled={isSavingProfile || isUploadingImage || isLoadingProfile}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input 
                type="email" 
                id="email" 
                value={contextEmail} 
                readOnly 
                disabled={isLoadingProfile} // Email is read-only, disabled during load
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input 
                type="tel" 
                id="phoneNumber" 
                value={currentPhoneNumber} 
                onChange={(e) => setCurrentPhoneNumber(e.target.value)} 
                disabled={isSavingProfile || isUploadingImage || isLoadingProfile}
              />
              {contextPhoneNumberHistory && contextPhoneNumberHistory.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Login Phone Numbers</span>
                  <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {contextPhoneNumberHistory.map((entry, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        background: '#f1f5f9',
                        borderRadius: '6px',
                        fontSize: '13px',
                      }}>
                        <span style={{ fontWeight: 500, color: '#334155', letterSpacing: '0.5px' }}>{entry.number}</span>
                        <span style={{ color: '#94a3b8', fontSize: '11px' }}>{new Date(entry.capturedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </FormGroup>
            <SubmitButton 
              type="submit" 
              disabled={isSavingProfile || isUploadingImage || isLoadingProfile || !currentFullName.trim()}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSavingProfile ? 'Saving...' : 'Save Profile Changes'}
            </SubmitButton>
          </FormFieldsSection>
        </ProfileContainer>
      </Form>
      )}
    </ProfileSettingsContainer>
  );
};

export default UserProfileSettings;
